/**
 * Tells the query cache about the auth_session a sign-in just opened (review row #104).
 *
 * The shell's guard asks `authSessionQuery` who is signed in, through `ensureQueryData` — which
 * answers from the cache whenever the cache holds anything, stale or not. A visit that STARTED
 * behind the guard (the site root, a bookmarked `/agents`) has already cached the anonymous
 * answer, `null`, by the time it lands on `/log-in`. A sign-in happens outside that query, so
 * unless the entry screen writes the new auth_session into the cache before it navigates, the
 * guard reads the old `null`, redirects to `/log-in`, and the person — though signed in — never
 * leaves the entry screen.
 *
 * Every success path on the entry screens goes through this: log in, sign up, choosing one of
 * several accounts, finishing setup, and accepting an invitation on `/join`. (Fix-round package L
 * fixed the same defect as "CL #104" by dropping the cached answer; the merge of 2026-09-26 kept
 * this one mechanism, which also covers the invitation path L had fixed.) The auth_session the API just returned is the
 * authoritative answer, so it is written rather than invalidated: no second `GET /v1/session`,
 * and no window in which the guard can read the stale value.
 *
 * Everything else cached is dropped first, as log-out's `queryClient.clear()` does: a sign-in
 * starts a new identity, and a row cached under the previous one (an auth_session that expired
 * without a log-out, then a different person signing in in the same tab) must not be shown to
 * the new one. Queries only — the mutation that is calling this is still running.
 */
import { useQueryClient } from "@tanstack/react-query";

import type { AuthSessionView } from "../../api/gateway.js";
import { QUERY_KEY } from "../../api/query.js";

export function useAdoptAuthSession(): (opened: AuthSessionView) => void {
  const queryClient = useQueryClient();
  return (opened) => {
    // Keep the active auth observer attached; replacing a removed query would strand it on the guest answer.
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== QUERY_KEY.authSession[0] });
    queryClient.setQueryData<AuthSessionView | null>(QUERY_KEY.authSession, opened);
  };
}
